/**
 * Iter 122.help-pattern — Mobile-friendly contextual help.
 *
 * Replaces the hover-only radix Tooltip pattern (which silently fails on
 * touch devices) with a tap-driven panel:
 *
 *   • Desktop ( ≥640px ) — opens an unstyled-but-themed `Popover` anchored
 *     to the trigger, dismisses on outside-click / Escape.
 *   • Mobile  ( <640px  ) — opens a centred `Dialog` that is always inside
 *     the viewport, scrollable if long, with a clear close button.
 *
 * Two trigger variants share the same panel so the rest of the codebase
 * keeps a single import surface:
 *
 *   <InfoTooltip text="One-liner."                />   ← compact `i` button
 *   <HelpLink title="Section name">…content…</HelpLink>← "Learn more" link
 *
 * The legacy prop shape (`text="…"`) is preserved for the dozen existing
 * call sites — no migration required.
 */

import { useEffect, useState, useId, isValidElement, Children } from "react";
import { Info, X, HelpCircle } from "lucide-react";
import {
  Popover,
  PopoverContent,
  PopoverTrigger,
} from "@/components/ui/popover";
import {
  Dialog,
  DialogContent,
  DialogTitle,
  DialogDescription,
} from "@/components/ui/dialog";

/** Cheap viewport check that re-evaluates on resize. Tailwind's `sm`
 *  breakpoint is 640px; we mirror that so behaviour and theming stay
 *  consistent with the rest of the design system. */
function useIsMobile() {
  const [m, setM] = useState(() =>
    typeof window !== "undefined" ? window.matchMedia("(max-width: 639px)").matches : false,
  );
  useEffect(() => {
    if (typeof window === "undefined") return undefined;
    const mq = window.matchMedia("(max-width: 639px)");
    const fn = (e) => setM(e.matches);
    mq.addEventListener?.("change", fn);
    return () => mq.removeEventListener?.("change", fn);
  }, []);
  return m;
}

/** Shared panel — wraps either a Dialog (mobile) or Popover (desktop). */
function HelpPanel({ trigger, title, children, panelTestid }) {
  const isMobile = useIsMobile();
  const [open, setOpen] = useState(false);
  const titleId = useId();

  if (isMobile) {
    return (
      <>
        <span onClick={(e) => { e.preventDefault(); e.stopPropagation(); setOpen(true); }}>
          {trigger}
        </span>
        <Dialog open={open} onOpenChange={setOpen}>
          <DialogContent
            className="max-w-[92vw] sm:max-w-md rounded-md p-0 overflow-hidden"
            data-testid={panelTestid || "help-panel"}
            aria-labelledby={titleId}
          >
            <div className="flex items-start justify-between gap-3 px-4 pt-4 pb-2 border-b border-[#D9E1E3]">
              <div className="flex items-center gap-2 min-w-0">
                <HelpCircle className="w-4 h-4 text-[#1F9FA4] flex-shrink-0" strokeWidth={1.8} />
                <DialogTitle id={titleId} className="text-sm font-medium text-[#2E3A3F] truncate">
                  {title || "Information"}
                </DialogTitle>
              </div>
              <button
                type="button"
                onClick={() => setOpen(false)}
                className="p-1 -m-1 rounded-sm text-[#6F828A] hover:text-[#2E3A3F] focus-visible:outline focus-visible:outline-2 focus-visible:outline-[#1F9FA4]"
                aria-label="Close help"
                data-testid="help-panel-close"
              >
                <X className="w-4 h-4" />
              </button>
            </div>
            <DialogDescription asChild>
              <div className="px-4 py-3 text-sm leading-relaxed text-[#48595E] max-h-[60vh] overflow-y-auto">
                {children}
              </div>
            </DialogDescription>
          </DialogContent>
        </Dialog>
      </>
    );
  }

  // Desktop popover
  return (
    <Popover open={open} onOpenChange={setOpen}>
      <PopoverTrigger asChild>{trigger}</PopoverTrigger>
      <PopoverContent
        side="top"
        align="start"
        className="max-w-sm text-sm leading-relaxed bg-white border border-[#D9E1E3] text-[#2E3A3F] shadow-md p-3"
        data-testid={panelTestid || "help-panel"}
      >
        {title ? (
          <div className="flex items-center gap-1.5 mb-1.5">
            <HelpCircle className="w-3.5 h-3.5 text-[#1F9FA4] flex-shrink-0" strokeWidth={1.8} />
            <span className="text-xs font-medium text-[#2E3A3F]">{title}</span>
          </div>
        ) : null}
        {children}
      </PopoverContent>
    </Popover>
  );
}

/** Compact `i` button trigger — the legacy entry point. Accepts the same
 *  `text` / `testid` / `className` props as before so existing call sites
 *  do not need to change. Renders an explicit `<button>` to keep the touch
 *  target ≥24×24 — small but big enough on mobile because tapping anywhere
 *  inside opens the panel. */
export function InfoTooltip({ text, title, testid, className = "", children }) {
  const trigger = (
    <button
      type="button"
      className={`inline-flex items-center justify-center w-6 h-6 -m-1 rounded-full text-[#6F828A] hover:text-[#1F9FA4] focus-visible:text-[#1F9FA4] focus-visible:outline-none transition-colors ${className}`}
      aria-label="More information"
      data-testid={testid || "info-tooltip"}
    >
      <Info className="w-3.5 h-3.5" strokeWidth={1.8} />
    </button>
  );
  // Render content: explicit children if provided, otherwise the `text` prop.
  const content = hasContent(children) ? children : (text ? <p>{text}</p> : null);
  return (
    <HelpPanel trigger={trigger} title={title} panelTestid={testid ? `${testid}-panel` : undefined}>
      {content}
    </HelpPanel>
  );
}

/** "Learn more" text-link trigger — the new primary affordance. Renders
 *  a calm, discoverable inline link rather than a tiny `i` icon so the
 *  help is more obvious and the touch target is larger. */
export function HelpLink({
  label = "Learn more",
  title,
  testid,
  className = "",
  children,
  inline = true,
}) {
  const trigger = (
    <button
      type="button"
      className={`inline-flex items-center gap-1 text-xs font-medium text-[#116B6E] hover:text-[#0F5658] focus-visible:outline focus-visible:outline-2 focus-visible:outline-[#1F9FA4] rounded-sm ${inline ? "" : "px-2 py-1"} ${className}`}
      aria-label={`${label} about ${title || "this section"}`}
      data-testid={testid || "help-link"}
    >
      <HelpCircle className="w-3 h-3" strokeWidth={1.8} />
      <span className="underline underline-offset-2 decoration-dotted">{label}</span>
    </button>
  );
  return (
    <HelpPanel trigger={trigger} title={title} panelTestid={testid ? `${testid}-panel` : undefined}>
      {children}
    </HelpPanel>
  );
}

function hasContent(children) {
  if (children == null) return false;
  if (Array.isArray(children)) return children.some(hasContent);
  if (isValidElement(children)) return true;
  if (typeof children === "string") return children.trim().length > 0;
  return Children.count(children) > 0;
}

export default InfoTooltip;
